import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';

// ---------------------------------------------------------------------------
// API response schemas
// ---------------------------------------------------------------------------

const DashboardKPISchema = z.object({
  active_clients: z.number(),
  pending_instructions: z.number(),
  fx_trades_inflight: z.number(),
  total_revenue_ytd: z.string(),
  incoming_pending: z.number(),
});

const RevenueDataPointSchema = z.object({
  date: z.string(),
  amount: z.number(),
});

const RevenueDataSchema = z.object({
  revenue_by_date: z.array(RevenueDataPointSchema),
  currency: z.string(),
});

export type DashboardKPIData = z.infer<typeof DashboardKPISchema>;
export type RevenueDataPoint = z.infer<typeof RevenueDataPointSchema>;
export type RevenueData = z.infer<typeof RevenueDataSchema>;

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const DASHBOARD_KEY = ['dashboard'] as const;
const REVENUE_TIMESERIES_KEY = ['revenue-timeseries'] as const;

const REFETCH_INTERVAL = 10_000; // 10 seconds

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseDashboardReturn {
  kpis: DashboardKPIData | undefined;
  revenue: RevenueData | undefined;
  isLoadingKpis: boolean;
  isLoadingRevenue: boolean;
  kpisError: Error | null;
  revenueError: Error | null;
  refresh: () => void;
  isRefreshing: boolean;
}

export function useDashboard(): UseDashboardReturn {
  const queryClient = useQueryClient();

  const kpisQuery = useQuery<DashboardKPIData>({
    queryKey: DASHBOARD_KEY,
    queryFn: async () => {
      const raw = await api.get<DashboardKPIData>('/api/dashboard');
      return DashboardKPISchema.parse(raw);
    },
    refetchInterval: REFETCH_INTERVAL,
  });

  const revenueQuery = useQuery<RevenueData>({
    queryKey: REVENUE_TIMESERIES_KEY,
    queryFn: async () => {
      const raw = await api.get<RevenueData>('/api/revenue/timeseries');
      return RevenueDataSchema.parse(raw);
    },
    refetchInterval: REFETCH_INTERVAL,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY });
    void queryClient.invalidateQueries({ queryKey: REVENUE_TIMESERIES_KEY });
  };

  return {
    kpis: kpisQuery.data,
    revenue: revenueQuery.data,
    isLoadingKpis: kpisQuery.isLoading,
    isLoadingRevenue: revenueQuery.isLoading,
    kpisError: kpisQuery.error,
    revenueError: revenueQuery.error,
    refresh,
    isRefreshing: kpisQuery.isFetching || revenueQuery.isFetching,
  };
}
